import React, { FC, Fragment, useState, useContext, ChangeEvent } from 'react';
import { Box, Fab, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import InvoiceDetailsSettingsForm from './invoice-details-settings-form/invoice-details-settings-form.components';
import InvoiceLabSettings from './invoice-lab-settings/invoice-lab-settings.components';
import EInvoicingSettings from './e-invoicing-settings/e-invoicing-settings.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { useMutation, useQuery } from '@apollo/client';
import { updateOrgInvoicingSettingTemplate } from 'apollo/templates/organization';
import { Organization } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import {
    isUserFrontendFeedbackError,
    useDenthubErrorBoundary,
} from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { labEInvoicingTemplate } from 'apollo/templates/e-invoicing';
import { labInvoiceSettingsTemplate, labVatNumberWarningTemplate } from 'apollo/templates/organization/query.graphql';
import { getEscapedMessage } from 'utils/escaped-message';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { numberFieldText } from './invoice-details-settings-form/payment-details-form-validation';

const InvoiceSettings: FC = () => {
    const { t: translate } = useTranslation(['lab-settings']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [currentLab, setCurrentLab] = useState<Organization>();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [configuration, setConfiguration] = useState<{
        invoiceNumberType: string | undefined;
        currentStartingNumber: number | undefined;
    }>({
        invoiceNumberType: '',
        currentStartingNumber: 0,
    });
    const [invoiceText, setInvoiceText] = useState('');
    const [detailsForm, setDetailsForm] = useState<InvoiceDetailsSettingsFormData>();
    const [isDetailsFormValid, setIsDetailsFormValid] = useState(false);

    const { loading } = useQuery(labInvoiceSettingsTemplate, {
        fetchPolicy: 'network-only',
        onError(error) {
            showBoundary(error);
        },
        onCompleted({ currentLab }) {
            setCurrentLab(currentLab);
            setConfiguration(currentLab.invoicing.configuration || {});
            setInvoiceText((currentLab.invoicing.invoiceText as string)?.replace(new RegExp('<br/>', 'gi'), '\n'));
        },
    });

    const handleInvoiceNumberType = (value: string | undefined) => {
        setConfiguration({
            currentStartingNumber: configuration?.currentStartingNumber,
            invoiceNumberType: value,
        });
    };
    const handleStartNumber = (value: number | undefined) => {
        setConfiguration({
            currentStartingNumber: value,
            invoiceNumberType: configuration.invoiceNumberType,
        });
    };

    const handleInvoiceText = (event: ChangeEvent<HTMLInputElement>) => {
        setInvoiceText(event.target.value);
    };

    const [updateOrgInvoicingSetting, { error }] = useMutation(updateOrgInvoicingSettingTemplate, {
        // DD-3700: a saved VAT number clears the missing-VAT-number warning. DD-3726: the
        // saved details decide what the lab still lacks for electronic invoices.
        refetchQueries: [labVatNumberWarningTemplate, labEInvoicingTemplate],
        // DD-3726: a refusal the lab can act on (e.g. a detail electronic invoices need) is
        // shown translated in the feedback dialog; anything else stays in the snackbar.
        onError: (mutationError) => {
            if (isUserFrontendFeedbackError(mutationError)) {
                showBoundary(mutationError);
            }
        },
        onCompleted: () => {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('common:CHANGES_SAVED'),
            });
        },
    });

    const handleSaveChanges = async () => {
        if (detailsForm) {
            const formattedInvoiceText = getEscapedMessage(invoiceText);
            const labInvoiceSettings = {
                vatNo: detailsForm.vatNo,
                invoiceAddress: detailsForm.invoiceAddress,
                costCenter: detailsForm.costCenter,
                paymentDetails: {
                    postgiro: detailsForm.postgiro,
                    bankgiro: detailsForm.bankgiro,
                    bicSwift: detailsForm.bicSwift,
                    iban: detailsForm.iban,
                    paymentBoxAccount: detailsForm.paymentBoxAccount || undefined,
                    accountNumber: detailsForm.accountNumber.trim() || undefined,
                    kid: detailsForm.kidEnabled
                        ? {
                              enabled: true,
                              length: Number(detailsForm.kidLength),
                              checkDigitMethod: detailsForm.kidCheckDigitMethod,
                          }
                        : undefined,
                    registrationNumber: detailsForm.registrationNumber.trim() || undefined,
                    referenceFormat: detailsForm.referenceFormat || undefined,
                    fi: detailsForm.fiEnabled
                        ? {
                              enabled: true,
                              cardType: detailsForm.fiCardType,
                              creditorNumber: detailsForm.fiCreditorNumber.trim(),
                          }
                        : undefined,
                },
                showForetaksregisteret: detailsForm.showForetaksregisteret,
                latePaymentInterest: numberFieldText(detailsForm.latePaymentInterest)
                    ? Number(detailsForm.latePaymentInterest)
                    : undefined,
                complaintPeriodDays: numberFieldText(detailsForm.complaintPeriodDays)
                    ? Number(detailsForm.complaintPeriodDays)
                    : undefined,
                taxNumber: detailsForm.taxNumber.trim() || undefined,
                companyRegister: {
                    registeredOffice: detailsForm.registeredOffice.trim() || undefined,
                    court: detailsForm.registerCourt.trim() || undefined,
                    number: detailsForm.registerNumber.trim() || undefined,
                    managingDirectors: detailsForm.managingDirectors.trim() || undefined,
                },
                smallBusinessExemption: detailsForm.smallBusinessExemption,
                communication: {
                    email: detailsForm.email,
                    phone: detailsForm.phone,
                    site: detailsForm.site,
                },
                defaultSetting: {
                    invoiceType: detailsForm.invoiceType,
                    invoiceDelivery: detailsForm.invoiceDelivery,
                    invoicePeriod: detailsForm.invoicePeriod,
                    paymentTerms: detailsForm.paymentTerms,
                },
                configuration: configuration,
                invoiceText: formattedInvoiceText,
            };

            updateOrgInvoicingSetting({
                variables: { OrgInvoicingSettingData: labInvoiceSettings },
            });
        }
    };

    const disableSave = () => {
        return (
            !isDetailsFormValid ||
            !configuration?.invoiceNumberType ||
            (configuration?.invoiceNumberType === 'INCREMENTAL' && !configuration?.currentStartingNumber)
        );
    };

    const handleDetailsChange = (formData: InvoiceDetailsSettingsFormData, isValid: boolean) => {
        setDetailsForm(formData);
        setIsDetailsFormValid(isValid);
    };

    return (
        <Fragment>
            {error && !isUserFrontendFeedbackError(error) ? (
                <AlertSnackbar type="error" message={error?.message} />
            ) : undefined}
            <PageTitle headingStyle="h2" title={translate('lab-settings:INVOICING')} />
            {!loading && currentLab ? (
                <Fragment>
                    <Box mb={3}>
                        <InvoiceDetailsSettingsForm currentLab={currentLab} onChange={handleDetailsChange} />
                    </Box>
                    <Box mb={3}>
                        <InvoiceLabSettings
                            currentLab={currentLab!}
                            onInvoiceNumberTypeChange={handleInvoiceNumberType}
                            onStartNumberChange={handleStartNumber}
                        />
                    </Box>
                    <Box mb={3}>
                        <PageTitle headingStyle="h3" title={translate('lab-settings:INVOICE_TEXT')} />
                        <TextField
                            fullWidth
                            margin="dense"
                            label={translate('lab-settings:INVOICE_TEXT')}
                            onChange={handleInvoiceText}
                            value={invoiceText}
                            multiline
                            rows={6}
                        />
                    </Box>
                    {/* DD-3726: last, above the floating save button, which it does not use. */}
                    <Box mb={10}>
                        <EInvoicingSettings />
                    </Box>

                    <Fab
                        sx={{ position: 'fixed', left: '50%', bottom: 20, zIndex: 100 }}
                        variant="extended"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={disableSave()}
                    >
                        <SaveOutlinedIcon sx={{ marginRight: 1 }} />
                        {translate('lab-settings:SAVE_CHANGES')}
                    </Fab>
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default InvoiceSettings;
